/**
 * Generator registry: maps a TopicId to its generator implementation.
 *
 * Screens and engines look topics up here rather than importing generators
 * directly, so adding a topic is a one-line registration. Topics without a
 * generator yet are simply absent; callers check `hasGenerator` and can render
 * a "coming soon" state, which lets the full curriculum structure exist in the
 * UI while generators are filled in incrementally.
 */

import type { Generator } from "./types";
import type { TopicId } from "../curriculum/curriculum";
import { variablesBothSides } from "./variables-both-sides";
import { multiStepEquations } from "./multi-step-equations";
import { twoStepFractions } from "./two-step-fractions";
import { proportions } from "./proportions";
import { absoluteValueEquations } from "./absolute-value-equations";
import { literalEquations } from "./literal-equations";
import { translatingEquations } from "./translating-equations";
import { multiStepInequalities } from "./multi-step-inequalities";
import { compoundInequalities } from "./compound-inequalities";
import { absoluteValueInequalities } from "./absolute-value-inequalities";
import { writingGraphingInequalities } from "./writing-graphing-inequalities";
import { discreteContinuous } from "./discrete-continuous";
import { domainRange } from "./domain-range";
import { functionRules } from "./function-rules";
import { verticalLineTest } from "./vertical-line-test";
import { functionNotation } from "./function-notation";
import { zeros } from "./zeros";
import { graphingSlopeIntercept } from "./graphing-slope-intercept";

const REGISTRY = new Map<TopicId, Generator>();

function register(gen: Generator): void {
  REGISTRY.set(gen.topic, gen);
}

register(variablesBothSides);
register(multiStepEquations);
register(twoStepFractions);
register(proportions);
register(absoluteValueEquations);
register(literalEquations);
register(translatingEquations);
register(multiStepInequalities);
register(compoundInequalities);
register(absoluteValueInequalities);
register(writingGraphingInequalities);
register(discreteContinuous);
register(domainRange);
register(functionRules);
register(verticalLineTest);
register(functionNotation);
register(zeros);
register(graphingSlopeIntercept);

export function getGenerator(topic: TopicId): Generator | undefined {
  return REGISTRY.get(topic);
}

export function hasGenerator(topic: TopicId): boolean {
  return REGISTRY.has(topic);
}

export function implementedTopics(): readonly TopicId[] {
  return [...REGISTRY.keys()];
}
